// ============================================================================
// marketDispute.ts — СПОРЫ МАРКЕТПЛЕЙСА (операторский арбитраж) — данные.
//
// Серверная модель НЕ меняется (proto/миграции не трогаем). Спор опирается на
// существующие механизмы платформы:
//   • СОСТОЯНИЕ — теги на заявке (TagService): «Спор маркетплейса» (открыт) и
//     «Спор маркетплейса: решён» (закрыт). Очередь оператора — ListSubjectsByTag;
//   • ИСТОРИЯ/КОММЕНТАРИИ — ОБСУЖДЕНИЕ ЗАЯВКИ (контекстный чат ядра:
//     context_type=document, context_code=mkt_requests, context_id=заявка).
//     Открытие/решение — размеченные сообщения, обычные сообщения — комментарии;
//   • ДЕНЬГИ — существующая серверная операция возврата (__action=refund на
//     mkt_requests, идемпотентность refund:<заявка>). «Выплата мастеру» и
//     «отказ» — РЕШЕНИЯ БЕЗ ДВИЖЕНИЯ ДЕНЕГ: стоимость взятия не возвращается,
//     расчёт за работу идёт между сторонами (в платформе нет удержания сумм).
//
// Права: открыть спор может автор заявки (заказчик) или назначенный мастер.
// Очередь/решение — «Оператор маркетплейса» или администратор (проверка экрана;
// денежную операцию окончательно разрешает СЕРВЕР).
//
// При открытии спора участники с ролью оператора/администратора добавляются в
// обсуждение заявки существующим RPC — иначе они не смогут прочитать историю.
// ============================================================================

import { getSDK } from '@/store/sdkStore';
import { useAuthStore } from '@/store/authStore';
import { logSwallowed } from '@/lib/errors';
import {
  getOrCreateContextThread,
  listConversations,
} from '@/lib/chatBridge';
import { sendChatMessage } from '@/lib/chatQueue';
import { isEffectivelyOffline } from '@/lib/offlineFlag';
import { logEvent } from '@/lib/eventLog';
import { loadMarketMasterEntityId } from '@/lib/marketActions';
import { ensureArbitersInConversation } from './marketDisputeArbiters';
import {
  loadMarketOperatorRequestsByIds,
  refundMarketRequest,
  type MarketOperatorRequest,
} from '@/lib/marketOperator';
import { marketOperatorAccess } from '@/lib/marketRoles';
import {
  DISPUTE_CLOSED_TAG,
  DISPUTE_OPEN_TAG,
  applyDisputeTags,
  findDisputeTagIds,
  readDisputeTagState,
} from './marketDisputeTags';

export {
  DISPUTE_REASONS,
  DISPUTE_DECISIONS,
  DISPUTE_DECISION_LABELS,
  disputeReasonLabel,
  disputeRoleLabel,
  parseDisputeMarker,
  buildOpenMessage,
  buildDecisionMessage,
} from './marketDisputeMessages';
export type {
  DisputeDecision,
  DisputeOpenerRole,
  DisputeReason,
  DisputeMarker,
} from './marketDisputeMessages';

import {
  DISPUTE_DECISION_LABELS,
  buildDecisionMessage,
  buildOpenMessage,
  parseDisputeMarker,
  type DisputeDecision,
  type DisputeMarker,
  type DisputeOpenerRole,
  type DisputeReason,
} from './marketDisputeMessages';

export { DISPUTE_OPEN_TAG, DISPUTE_CLOSED_TAG } from './marketDisputeTags';
export { DISPUTE_SUBJECT_TYPE, DISPUTE_SUBJECT_CODE } from './marketDisputeConstants';

import { DISPUTE_SUBJECT_CODE, DISPUTE_SUBJECT_TYPE } from './marketDisputeConstants';

export type DisputeStatus = 'none' | 'open' | 'closed';

// ---------- Модель --------------------------------------------------------------

export interface DisputeHistoryItem {
  id: string;
  senderId: string;
  senderName: string;
  text: string;
  createdAt: string;
  isDeleted: boolean;
  marker: DisputeMarker | null;
}

export interface MarketDisputeSummary {
  requestId: string;
  status: DisputeStatus;
  openedByRole: DisputeOpenerRole | null;
  openedByName: string;
  openedById: string;
  openedAt: string;
  reason: string;
  comment: string;
  decision: DisputeDecision | null;
  decisionComment: string;
  decisionByName: string;
  decisionAt: string;
  lastMessage: string;
  conversationId: string;
}

export interface MarketDispute extends MarketDisputeSummary {
  history: DisputeHistoryItem[];
  /** Почему историю не удалось прочитать (нет доступа/связи) — показывается явно. */
  historyError: string;
  request: MarketOperatorRequest | null;
}

export interface MarketDisputeQueueItem {
  request: MarketOperatorRequest;
  status: 'open' | 'closed';
}

export interface OpenDisputeResult {
  conversationId: string;
  already: boolean;
  warnings: string[];
}

export interface ResolveDisputeResult {
  alreadyRefunded: boolean;
  /** Решение уже было принято — повторная запись не выполнялась. */
  alreadyResolved: boolean;
}

// ---------- Чтение ------------------------------------------------------------------

async function conversationFor(requestId: string): Promise<string> {
  const items = await listConversations({ contextType: 'document', contextId: requestId });
  const found = items.find((c: any) => String(c.contextId || c.context_id || '') === requestId);
  return found ? String(found.id || '') : '';
}

const HISTORY_PAGE_SIZE = 200;
// Запас пагинации: маркер открытия отделён от текущего момента даже в очень
// длинных обсуждениях. Больше — нечитаемая история и лишние запросы.
const HISTORY_MAX_PAGES = 20;

function historyItem(m: any): DisputeHistoryItem {
  const text = String(m.text || '');
  return {
    id: String(m.id || ''),
    senderId: String(m.senderId || ''),
    senderName: String(m.senderName || ''),
    text,
    createdAt: String(m.createdAt || ''),
    isDeleted: !!m.isDeleted,
    marker: m.isDeleted ? null : parseDisputeMarker(text),
  };
}

/**
 * История спора В ХРОНОЛОГИЧЕСКОМ порядке (старые → новые).
 *
 * Сервер отдаёт страницу НОВЫМИ первыми. Для границы приватности нужен маркер
 * открытия: листаем назад (before_ts), пока он не найден, и ОТБРАСЫВАЕМ всё,
 * что было до него. Раньше брался только последний экран сообщений — при
 * длинном обсуждении маркер терялся, и оператору показывалась переписка ДО
 * спора (утечка), а «последнее сообщение» определялось неверно.
 */
async function readHistory(conversationId: string, paginate = true): Promise<DisputeHistoryItem[]> {
  if (!conversationId) return [];
  let allDesc: DisputeHistoryItem[] = [];
  let beforeTs = '';
  const pages = paginate ? HISTORY_MAX_PAGES : 1;
  for (let page = 0; page < pages; page += 1) {
    const req: any = { conversationId, limit: HISTORY_PAGE_SIZE };
    if (beforeTs) req.beforeTs = beforeTs;
    const res: any = await getSDK().messenger.listMessages(req);
    const batch = ((res?.items || []) as any[]).map(historyItem);
    if (batch.length === 0) break;
    const openedAt = batch.findIndex((m) => m.marker?.kind === 'opened');
    if (openedAt >= 0) {
      // Текущая страница старше уже собранных; обрезаем её по маркеру открытия.
      allDesc.push(...batch.slice(0, openedAt + 1));
      break;
    }
    allDesc.push(...batch);
    if (batch.length < HISTORY_PAGE_SIZE) break;
    const oldest = batch[batch.length - 1];
    const next = oldest.createdAt;
    if (!next || next === beforeTs) break;
    beforeTs = next;
  }
  return allDesc.reverse();
}

/** Последняя метка открытия спора в истории (null — не открывался). */
function openedMarker(history: DisputeHistoryItem[]): DisputeHistoryItem | null {
  return history.find((m) => m.marker?.kind === 'opened') || null;
}

/** Последняя метка решения в истории (null — решения ещё нет). */
function decisionMarker(history: DisputeHistoryItem[]): DisputeHistoryItem | null {
  const decisions = history.filter((m) => m.marker?.kind === 'decision');
  return decisions.length > 0 ? decisions[decisions.length - 1] : null;
}

/** Статус по ИСТОРИИ (источник истины) — теги лишь очередь оператора. */
function stateFromHistory(history: DisputeHistoryItem[], tags: { open: boolean; closed: boolean }): DisputeStatus {
  if (decisionMarker(history)) return 'closed';
  if (openedMarker(history)) return 'open';
  return tags.open ? 'open' : tags.closed ? 'closed' : 'none';
}

/**
 * Состояние спора по заявке. Без истории — для бейджа в чате (дешёвое чтение).
 */
export async function loadMarketDisputeSummary(requestId: string): Promise<{
  status: DisputeStatus;
  conversationId: string;
}> {
  if (!requestId) return { status: 'none', conversationId: '' };
  const state = await readDisputeTagState(requestId);
  let conversationId = '';
  let status: DisputeStatus = state.open ? 'open' : state.closed ? 'closed' : 'none';
  if (state.open || state.closed) {
    try {
      conversationId = await conversationFor(requestId);
      if (conversationId) {
        // Сводка — дешёвое чтение: достаточно последней страницы (решение и
        // метка открытия у активного спора всегда в конце обсуждения).
        status = stateFromHistory(await readHistory(conversationId, false), state);
      }
    } catch (e) {
      logSwallowed(e, 'Спор маркетплейса: обсуждение заявки');
    }
  }
  return { status, conversationId };
}

/** Полная карточка спора: состояние, история, решение, заявка. */
export async function loadMarketDispute(requestId: string, opts: { withRequest?: boolean } = {}): Promise<MarketDispute> {
  if (!requestId) throw new Error('Не указана заявка');
  const state = await readDisputeTagState(requestId);

  let conversationId = '';
  let historyError = '';
  let history: DisputeHistoryItem[] = [];
  if (state.open || state.closed) {
    try {
      conversationId = await conversationFor(requestId);
      if (!conversationId) {
        historyError = 'Обсуждение заявки не найдено — история спора недоступна.';
      } else {
        history = await readHistory(conversationId);
      }
    } catch (e) {
      historyError = 'Нет доступа к обсуждению заявки — историю спора прочитать нельзя.';
      logSwallowed(e, 'Спор маркетплейса: чтение истории');
    }
  }

  // ГРАНИЦА ПРИВАТНОСТИ: оператору показывается история С МОМЕНТА открытия
  // спора (переписка до спора не раскрывается). Нет метки (битые данные) —
  // показываем всё, что доступно.
  const openedIndex = history.findIndex((m) => m.marker?.kind === 'opened');
  const scoped = openedIndex >= 0 ? history.slice(openedIndex) : history;
  const opened = openedMarker(scoped);
  const decisionItem = decisionMarker(scoped);
  const lastPlain = [...scoped].reverse().find((m) => !m.marker && !m.isDeleted);

  // СОСТОЯНИЕ — по истории (SSoT), теги только отражают очередь. Рассинхрон
  // (метка решения есть, тег остался «открыт» — сбой записи тегов) лечим
  // детерминированно, иначе спор навсегда застревает в очереди.
  const status = stateFromHistory(scoped, state);
  const tagsStale = state.open !== (status === 'open') || state.closed !== (status === 'closed');
  if (tagsStale && status !== 'none') {
    try {
      await applyDisputeTags(requestId, status === 'open', status === 'closed');
    } catch (e) {
      logSwallowed(e, 'Спор маркетплейса: выравнивание метки состояния');
    }
  }

  let request: MarketOperatorRequest | null = null;
  if (opts.withRequest) {
    try {
      const list = await loadMarketOperatorRequestsByIds([requestId]);
      request = list[0] || null;
    } catch (e) {
      logSwallowed(e, 'Спор маркетплейса: заявка');
    }
  }

  return {
    requestId,
    status,
    openedByRole: opened?.marker?.role || null,
    openedByName: opened?.senderName || '',
    openedById: opened?.senderId || '',
    openedAt: opened?.createdAt || '',
    reason: opened?.marker?.reason || '',
    comment: opened?.marker?.comment || '',
    decision: decisionItem?.marker?.decision || null,
    decisionComment: decisionItem?.marker?.comment || '',
    decisionByName: decisionItem?.senderName || '',
    decisionAt: decisionItem?.createdAt || '',
    lastMessage: lastPlain?.text || '',
    conversationId,
    history: scoped,
    historyError,
    request,
  };
}

/** Очередь оператора: заявки с тегом «открыт»/«решён» (свежие — первыми). */
export async function loadMarketDisputeQueue(status: 'open' | 'closed'): Promise<MarketDisputeQueueItem[]> {
  const tagName = status === 'open' ? DISPUTE_OPEN_TAG : DISPUTE_CLOSED_TAG;
  const tagIds = await findDisputeTagIds(tagName);
  if (tagIds.length === 0) return [];
  const ids: string[] = [];
  for (const tagId of tagIds) {
    const res: any = await getSDK().tags.listSubjectsByTag({
      tagId,
      subjectType: DISPUTE_SUBJECT_TYPE,
      limit: 200,
    });
    for (const subject of (res?.subjects || []) as any[]) {
      const code = String(subject.subjectCode || '');
      const id = String(subject.subjectId || '');
      if (id && (!code || code === DISPUTE_SUBJECT_CODE) && !ids.includes(id)) ids.push(id);
    }
  }
  if (ids.length === 0) return [];
  const requests = await loadMarketOperatorRequestsByIds(ids.slice(0, 100));
  const byId = new Map(requests.map((r) => [r.id, r]));
  return ids
    .map((id) => byId.get(id))
    .filter((r): r is MarketOperatorRequest => !!r)
    .map((request) => ({ request, status }));
}

/** Фактическая роль участника заявки: заказчик (автор) или назначенный мастер. */
export function disputeRoleFor(
  request: { createdBy: string; masterId: string },
  userId: string,
  masterUserId: string,
): DisputeOpenerRole | null {
  if (!userId) return null;
  if (request.createdBy && request.createdBy === userId) return 'client';
  if (masterUserId && masterUserId === userId) return 'master';
  return null;
}

/** Права участника: заказчик (автор) или назначенный мастер. */
export function canOpenMarketDispute(
  request: { createdBy: string; masterId: string },
  userId: string,
  masterUserId: string,
): boolean {
  return disputeRoleFor(request, userId, masterUserId) !== null;
}

/** Права на решение: оператор маркетплейса или администратор (роли из JWT). */
export function canResolveMarketDispute(rolesInput: unknown): boolean {
  return marketOperatorAccess(rolesInput).isOperator;
}

// ---------- Запись ------------------------------------------------------------------

export interface OpenDisputeInput {
  requestId: string;
  requestTitle: string;
  reason: DisputeReason;
  comment: string;
  myId: string;
}

/** Открыть спор: сообщение в обсуждении + тег «открыт». Повтор идемпотентен. */
export async function openMarketDispute(input: OpenDisputeInput): Promise<OpenDisputeResult> {
  const comment = input.comment.trim();
  if (!input.requestId) throw new Error('Не указана заявка');
  if (!input.myId) throw new Error('Открытие спора требует входа в систему');
  if (!comment) throw new Error('Опишите ситуацию — без комментария спор не открывается');
  if (isEffectivelyOffline()) throw new Error('Открытие спора требует связи с сервером');

  // Права участника проверяются по САМОЙ заявке (автор/назначенный мастер), а не
  // по присланной роли: подменить роль в запросе невозможно.
  const request = (await loadMarketOperatorRequestsByIds([input.requestId]))[0];
  if (!request) throw new Error('Заявка не найдена — открыть спор нельзя');
  let masterUserId = '';
  if (request.masterId) {
    try {
      masterUserId = await loadMarketMasterEntityId(request.masterId);
    } catch (e) {
      logSwallowed(e, 'Спор маркетплейса: учётная запись мастера');
    }
  }
  const role = disputeRoleFor(request, input.myId, masterUserId);
  if (!role) throw new Error('Открыть спор может только заказчик или назначенный мастер');

  const state = await readDisputeTagState(input.requestId);
  if (state.closed) throw new Error('Спор по заявке уже решён — повторно открыть нельзя');
  if (state.open) {
    const conversationId = await conversationFor(input.requestId).catch(() => '');
    return { conversationId, already: true, warnings: [] };
  }

  const thread = await getOrCreateContextThread({
    objectType: 'document',
    objectCode: DISPUTE_SUBJECT_CODE,
    objectId: input.requestId,
    title: input.requestTitle || 'Обсуждение заявки',
  });
  const warnings = await ensureArbitersInConversation(thread.conversationId, input.myId);

  await getSDK().messenger.sendMessage({
    conversationId: thread.conversationId,
    text: buildOpenMessage(role, input.reason, comment),
    clientRef: `dispute-open-${input.requestId}`,
  } as any);
  try {
    await applyDisputeTags(input.requestId, true, false);
  } catch (e) {
    // Сообщение уже в истории (SSoT решения); метка очереди — производная.
    // Повторное открытие идемпотентно по clientRef и допишет метку.
    logSwallowed(e, 'Спор маркетплейса: метка открытия');
    warnings.push('Спор открыт, но очередь оператора не обновилась — повторите открытие.');
  }
  // Аудит вне редактируемого чата (журнал регистрации; серверное время).
  void logEvent({
    level: 'info',
    category: 'data',
    message: `Открыт спор по заявке ${input.requestId}`,
    entityType: 'document',
    entityCode: DISPUTE_SUBJECT_CODE,
    objectId: input.requestId,
    details: JSON.stringify({
      role,
      reason: input.reason,
      comment,
      conversation_id: thread.conversationId,
    }),
  }).catch((e) => logSwallowed(e, 'Спор маркетплейса: журнал открытия'));
  return { conversationId: thread.conversationId, already: false, warnings };
}

export interface ResolveDisputeInput {
  requestId: string;
  decision: DisputeDecision;
  comment: string;
  requestTitle: string;
}

/**
 * Решение оператора. «Возврат» выполняет существующую серверную операцию
 * возврата; «выплата»/«отказ» — фиксация решения без движения денег.
 * Возврат идемпотентен на сервере — повторный вызов не удваивает зачисление.
 */
export async function resolveMarketDispute(input: ResolveDisputeInput): Promise<ResolveDisputeResult> {
  const comment = input.comment.trim();
  if (!input.requestId) throw new Error('Не указана заявка');
  // ПРАВА: решение принимает ТОЛЬКО оператор маркетплейса/администратор
  // (денежную операцию окончательно разрешает сервер; здесь — гейт экрана/слоя).
  if (!canResolveMarketDispute(useAuthStore.getState().user?.roles)) {
    throw new Error('Решение по спору принимает оператор маркетплейса или администратор');
  }
  if (!comment) throw new Error('Укажите обоснование решения');
  if (isEffectivelyOffline()) throw new Error('Решение по спору требует связи с сервером');

  const state = await readDisputeTagState(input.requestId);
  let conversationId = '';
  let history: DisputeHistoryItem[] = [];
  if (!state.closed) {
    try {
      conversationId = await conversationFor(input.requestId);
    } catch (e) {
      logSwallowed(e, 'Спор маркетплейса: обсуждение заявки');
    }
    if (conversationId) {
      try {
        history = await readHistory(conversationId);
      } catch (e) {
        logSwallowed(e, 'Спор маркетплейса: чтение истории');
        throw new Error('Нет доступа к обсуждению заявки — решение записать нельзя');
      }
    }
  }

  // ИДЕМПОТЕНТНОСТЬ: повторное решение НИЧЕГО не делает (в т.ч. не двигает
  // деньги). Источник истины — метка решения в истории, тег лишь производная.
  const priorDecision = decisionMarker(history);
  if (state.closed || priorDecision) {
    if (priorDecision && !state.closed) {
      try {
        await applyDisputeTags(input.requestId, false, true);
      } catch (e) {
        logSwallowed(e, 'Спор маркетплейса: метка решения');
      }
    }
    return { alreadyRefunded: false, alreadyResolved: true };
  }
  // Метка «открыт» могла не записаться (сбой тегов) — история её подтверждает.
  if (!state.open && !openedMarker(history)) {
    throw new Error('Спор по заявке не открыт — принимать решение нечему');
  }
  if (!conversationId) throw new Error('Обсуждение заявки не найдено — решение не записано');

  let alreadyRefunded = false;
  if (input.decision === 'refund') {
    const result = await refundMarketRequest(
      input.requestId,
      `Спор: ${DISPUTE_DECISION_LABELS.refund}`,
      comment,
    );
    if (!result.ok) {
      throw new Error(result.message || 'Не удалось выполнить возврат по заявке');
    }
    alreadyRefunded = result.already;
  }

  await getSDK().messenger.sendMessage({
    conversationId,
    text: buildDecisionMessage(input.decision, comment),
    clientRef: `dispute-decision-${input.requestId}-${input.decision}`,
  } as any);

  try {
    await applyDisputeTags(input.requestId, false, true);
  } catch (e) {
    // Решение уже в истории (SSoT) — метку очереди выровняет loadMarketDispute.
    logSwallowed(e, 'Спор маркетплейса: метка решения');
  }
  // Аудит решения — независимо от редактируемых сообщений чата.
  void logEvent({
    level: 'info',
    category: 'security',
    message: `Решение по спору (${DISPUTE_DECISION_LABELS[input.decision]}) по заявке ${input.requestId}`,
    entityType: 'document',
    entityCode: DISPUTE_SUBJECT_CODE,
    objectId: input.requestId,
    details: JSON.stringify({
      decision: input.decision,
      comment,
      refund_already: alreadyRefunded,
    }),
  }).catch((e) => logSwallowed(e, 'Спор маркетплейса: журнал решения'));
  return { alreadyRefunded, alreadyResolved: false };
}

/** Комментарий участника/оператора в обсуждении заявки (офлайн — в очередь). */
export async function sendMarketDisputeComment(
  requestId: string,
  text: string,
  myId: string,
  requestTitle: string,
): Promise<{ queued: boolean; conversationId: string }> {
  const clean = text.trim();
  if (!clean) throw new Error('Введите комментарий');
  if (!requestId) throw new Error('Не указана заявка');
  const thread = await getOrCreateContextThread({
    objectType: 'document',
    objectCode: DISPUTE_SUBJECT_CODE,
    objectId: requestId,
    title: requestTitle || 'Обсуждение заявки',
  });
  const result = await sendChatMessage({
    conversationId: thread.conversationId,
    text: clean,
    attachments: [],
    replyTo: '',
  }, myId);
  return { queued: result.queued, conversationId: thread.conversationId };
}
